import React from 'react';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import { SPACE_OVERVIEW as SPACE_OVERVIEW_MARKER } from '@confluence/screen';
import { SpaceOverviewSwitcherLoader } from '@confluence/space-overview/entry-points/SpaceOverview.ts';
import { CONTAINER_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { COMPANY_HUB, SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { SpaceBase } from '@confluence/space-base/entry-points/SpaceBase.ts';

export function getSpaceOverviewRoute(Component: React.ElementType): {
	(match: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS({ params: { spaceKey }, query: { mode } }: RouteMatch): {
		Screen: {
			screenEvent: {
				name: string;
				id: string;
				attributes: {
					isGlobalMode: boolean;
					navdexPointType: string;
					spaceKey: string;
				};
			};
			pageState: {
				spaceKey: string;
				contentId: any;
				routeName: string;
				contentType: string;
			};
		};
		MainLayout: {
			navView: string;
			spaceKey: string;
			isViewPage: boolean;
		};
	};
} {
	const SpaceOverviewRoute = (match: RouteMatch): React.JSX.Element => {
		const {
			params: { spaceKey = '', contentId = '' },
			query,
		} = match;

		if (isCompanyHubSpaceKey(spaceKey)) {
			return <Redirection name={COMPANY_HUB.name} />;
		}

		return (
			<SpaceBase>
				<Component spaceKey={spaceKey} contentId={contentId} query={query} />
			</SpaceBase>
		);
	};

	SpaceOverviewRoute.NAVIGATION_PARAMS = ({
		params: { spaceKey = '' },
		query: { mode = '' },
	}: RouteMatch): {
		Screen: {
			screenEvent: {
				name: string;
				id: string;
				attributes: {
					isGlobalMode: boolean;
					navdexPointType: string;
					spaceKey: string;
				};
			};
			pageState: {
				spaceKey: string;
				contentId: any;
				routeName: string;
				contentType: string;
			};
		};
		MainLayout: {
			navView: string;
			spaceKey: string;
			isViewPage: boolean;
		};
	} => ({
		Screen: {
			screenEvent: {
				name: 'spaceOverviewScreen',
				id: spaceKey,
				attributes: {
					isGlobalMode: mode === 'global',
					navdexPointType: START_TOUCH,
					spaceKey,
				},
			},
			pageState: {
				spaceKey,
				contentId: SPACE_OVERVIEW_MARKER,
				routeName: SPACE_OVERVIEW.name,
				contentType: 'page',
			},
		},
		MainLayout: {
			navView: CONTAINER_HOME_ID,
			spaceKey,
			isViewPage: true,
		},
	});

	return SpaceOverviewRoute;
}

export const SpaceOverviewRouteForSSR: {
	(match: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS({ params: { spaceKey }, query: { mode } }: RouteMatch): {
		Screen: {
			screenEvent: {
				name: string;
				id: string;
				attributes: {
					isGlobalMode: boolean;
					navdexPointType: string;
					spaceKey: string;
				};
			};
			pageState: {
				spaceKey: string;
				contentId: any;
				routeName: string;
				contentType: string;
			};
		};
		MainLayout: {
			navView: string;
			spaceKey: string;
			isViewPage: boolean;
		};
	};
} = getSpaceOverviewRoute(SpaceOverviewSwitcherLoader);
